import userAtom from "@/atoms/userAtom";
import ActionSubmitted from "@/components/methodology/ActionSubmitted";
import { DecisionsTab } from "@/components/methodology/approved/Decisions";
import ApproveRejectMethodology from "@/components/methodology/ApproveRejectMethodology";
import FeedbackTimeline from "@/components/methodology/FeedbackTimeline";
import ReviewMethodologyFeedbackComponent from "@/components/methodology/reviewMethodology/FeedbackFormComponent";
import SelectField from "@/components/shared/formFields/select";
import Button from "@/components/shared/ui/Button";
import { Loader } from "@/components/shared/ui/Loader";
import useGetMethodologyReviewComments from "@/hooks/methodologyReview/useGetMethodologyReviewComments";
import { useBasicMethodologyRoundDetails } from "@/hooks/methodologyReview/useMethodologyRoundDetails";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { UserRole } from "@/types/Role";
import { isOriginator, isReviewerOfGivenMethodology, userHasMinimumAccessLevel } from "@/utils/isOriginator";
import { useAtom } from "jotai/index";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useState } from "react";

const Comments = dynamic(() => import("@/components/methodology/reviewMethodology/Comment"));

interface ExpertReviewProps {
	roundDetails?: MethodologyWithRoundDetails;
	sortedRoundIds?: string[];
	role?: string;
}

const ExpertReview = (props: ExpertReviewProps) => {
	const [user] = useAtom(userAtom);
	const { roundDetails, sortedRoundIds, role } = props;
	const [selectedRoundId, setSelectedRoundId] = useState<string | undefined>(
		sortedRoundIds?.[sortedRoundIds?.length - 1],
	);
	const { isLoading: isLoadingComments } = useGetMethodologyReviewComments(selectedRoundId);
	const [isCommentsSectionShowing, setIsCommentsSectionShowing] = useState(false);
	// As this is a tab section, we can't rely on the query params roundid and the data passed from parent component
	// we have to fetch separately for selectedRoundId within this component's state
	const { data: tabRoundData, isLoading: isTabRoundDataLoading } = useBasicMethodologyRoundDetails({
		methodologyId: roundDetails?.id.toString(),
		roundId: selectedRoundId,
	});

	useEffect(() => {
		if (tabRoundData?.iscommentadded || tabRoundData?.methodologyRoundStatus === "EXPIRED") {
			setIsCommentsSectionShowing(true);
		} else {
			if (tabRoundData?.methodologyRoundStatus === "RUNNING" && tabRoundData?.reviewStatus === "ABSENT") {
				// we want to keep comment section open for nonvoting reviewer in final round
				// as he won't be able to comment in the final round
				setIsCommentsSectionShowing(true);
				return;
			}
			setIsCommentsSectionShowing(false);
		}
	}, [tabRoundData?.iscommentadded, tabRoundData?.methodologyRoundStatus]);

	const isReviewerRole = user && roundDetails && isReviewerOfGivenMethodology(user, roundDetails);
	const isAdminRole = role && role === UserRole.ADMIN;
	const isPDAndOriginator =
		roundDetails && user && userHasMinimumAccessLevel(UserRole.METHODOLOGY_CURATOR, user.rolesInfo)
			? isOriginator(user, roundDetails.originatorId)
			: false;

	return (
		<div className="space-y-6">
			<div>
				<h2>Expert Review</h2>
				{tabRoundData?.status !== "APPROVED" && tabRoundData && (
					<FeedbackTimeline methodologyDetails={tabRoundData} customRoundLength={7} hideDetailsIfRoundClosed={true} />
				)}
			</div>
			<div className="flex flex-wrap items-start">
				<div className="mb-6 mr-4 w-64">
					<SelectField
						onChange={(e) => {
							setSelectedRoundId(e.target.value);
							return Promise.resolve();
						}}
						name="round"
						onBlur={() => Promise.resolve()}
						value={selectedRoundId}
						label=""
						options={
							sortedRoundIds?.map((v, idx) => {
								if (tabRoundData?.rounds[idx]?.isAdditionalRound && idx == sortedRoundIds?.length - 1) {
									return {
										value: v,
										label: `Final Round`,
									};
								}
								return {
									value: v,
									label: `Round ${idx + 1}`,
								};
							}) ?? []
						}
					/>
				</div>
				<>
					{tabRoundData?.canPerformAction && (tabRoundData?.canApprove || tabRoundData?.canReject) && (
						<ApproveRejectMethodology methodologyDetails={tabRoundData} />
					)}
				</>
			</div>
			{isTabRoundDataLoading && <Loader />}
			{!isTabRoundDataLoading && (
				<div className="space-y-6">
					{isReviewerRole && <ActionSubmitted reviewStatus={tabRoundData?.reviewStatus} />}

					<div>
						{isReviewerRole &&
							isCommentsSectionShowing &&
							tabRoundData?.methodologyRoundStatus === "RUNNING" &&
							tabRoundData?.reviewStatus !== "ABSENT" && (
								<ReviewMethodologyFeedbackComponent
									methodologyDetails={tabRoundData}
									setIsFeedbackShowing={setIsCommentsSectionShowing}
								/>
							)}
					</div>

					{!isAdminRole &&
						((isReviewerRole && isCommentsSectionShowing) ||
							isPDAndOriginator ||
							tabRoundData?.status == "APPROVED") && (
							<Comments
								roundId={selectedRoundId}
								reviewers={tabRoundData?.reviewers ?? []}
								methodologyOriginatorId={tabRoundData?.originatorId!}
								canReply={false}
								isRoundRunning={tabRoundData?.methodologyRoundStatus === "RUNNING"}
							/>
						)}

					{!isLoadingComments &&
						isReviewerRole &&
						!isCommentsSectionShowing &&
						tabRoundData?.reviewStatus !== "ABSENT" && (
							<div className="relative mt-12 h-96 w-full items-center justify-center py-8">
								<div className="absolute left-1/2 top-1/2 h-full min-h-[30rem] w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 transform">
									<Image src="/blob.svg" alt="guide" layout="fill" />
								</div>

								<div className="absolute left-1/2 top-1/2  flex w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 transform flex-col items-center space-x-12 lg:flex-row">
									<Image src="/feedback.svg" alt="guide" width={460} height={285} />
									<div className="w-full max-w-xl space-y-4">
										<h4 className="font-bold">Help Shape this Methodology with Feedback</h4>
										<p>
											Post and reply to comments to improve the proposed methodology before it goes up for approval.
										</p>
										<Button onClick={() => setIsCommentsSectionShowing(true)}>Start Commenting</Button>
									</div>
								</div>
							</div>
						)}

					{isAdminRole && tabRoundData?.id && selectedRoundId && tabRoundData.status !== "APPROVED" && (
						<DecisionsTab methodologyId={tabRoundData?.id} roundId={Number(selectedRoundId)} />
					)}

					{/*comments in the admin's pov are shown in the bottommost div in review section*/}
					{/*doing the same here as well*/}
					{isAdminRole && (
						<Comments
							roundId={selectedRoundId}
							reviewers={tabRoundData?.reviewers ?? []}
							methodologyOriginatorId={tabRoundData?.originatorId!}
							canReply={false}
							isRoundRunning={tabRoundData?.methodologyRoundStatus === "RUNNING"}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default ExpertReview;
